import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

const Table = ({ 
  columns, 
  data, 
  onSort, 
  sortBy, 
  sortOrder, 
  className = '',
  emptyMessage = 'No data available',
  responsive = true,
  mobileBreakpoint = 768 // px
}) => {
  const { isMobile, isTablet, getResponsiveValue } = useTheme();
  const [expandedRows, setExpandedRows] = useState(new Set());

  const handleSort = (columnKey) => {
    if (onSort) {
      const newOrder = sortBy === columnKey && sortOrder === 'asc' ? 'desc' : 'asc';
      onSort(columnKey, newOrder);
    }
  };

  const toggleRowExpansion = (rowIndex) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowIndex)) {
      newExpanded.delete(rowIndex);
    } else {
      newExpanded.add(rowIndex);
    }
    setExpandedRows(newExpanded);
  };

  // Responsive configurations
  const containerClasses = getResponsiveValue({
    mobile: 'table-container rounded-xl',
    desktop: 'table-container'
  });

  const cellPadding = getResponsiveValue({
    mobile: 'px-3 py-3',
    tablet: 'px-4 py-3',
    desktop: 'px-6 py-4'
  });

  const headerCellPadding = getResponsiveValue({
    mobile: 'px-3 py-3',
    tablet: 'px-4 py-3',
    desktop: 'px-6 py-4'
  });

  const textSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  const headerTextSize = getResponsiveValue({
    mobile: 'text-xs',
    desktop: 'text-xs'
  });

  const emptyIconSize = getResponsiveValue({
    mobile: 'w-10 h-10',
    desktop: 'w-12 h-12'
  });

  // Determine which columns to show on mobile
  const primaryColumns = columns.filter(col => col.primary);
  const secondaryColumns = columns.filter(col => !col.primary);
  const visibleColumns = isMobile && responsive && primaryColumns.length > 0 
    ? primaryColumns 
    : columns;

  // Mobile card view
  if (isMobile && responsive) {
    return (
      <div className={`space-y-3 ${className}`}>
        {data.length === 0 ? (
          <div className="card p-6 text-center">
            <div className="flex flex-col items-center space-y-3">
              <svg className={`${emptyIconSize} text-text-quaternary`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-text-tertiary text-sm">{emptyMessage}</p>
            </div>
          </div>
        ) : (
          data.map((row, rowIndex) => (
            <div key={row.id || rowIndex} className="card p-4 space-y-3">
              {/* Primary columns always visible */}
              {primaryColumns.map((column) => (
                <div key={column.key} className="flex justify-between items-start">
                  <span className="text-text-secondary text-xs font-medium uppercase tracking-wider flex-shrink-0 w-1/3">
                    {column.label}
                  </span>
                  <div className="text-text-primary text-sm text-right flex-1 ml-2">
                    {column.render 
                      ? column.render(row[column.key], row, rowIndex)
                      : row[column.key]
                    }
                  </div>
                </div>
              ))}

              {/* Secondary columns - collapsible */}
              {secondaryColumns.length > 0 && (
                <>
                  <button
                    onClick={() => toggleRowExpansion(rowIndex)}
                    className="w-full flex items-center justify-center text-text-tertiary hover:text-text-primary text-xs py-2 border-t border-border-primary transition-colors"
                  >
                    {expandedRows.has(rowIndex) ? 'Show Less' : 'Show More'}
                    <svg 
                      className={`w-4 h-4 ml-1 transition-transform ${expandedRows.has(rowIndex) ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {expandedRows.has(rowIndex) && (
                    <div className="space-y-3 pt-2 border-t border-border-primary">
                      {secondaryColumns.map((column) => (
                        <div key={column.key} className="flex justify-between items-start">
                          <span className="text-text-secondary text-xs font-medium uppercase tracking-wider flex-shrink-0 w-1/3">
                            {column.label}
                          </span>
                          <div className="text-text-primary text-sm text-right flex-1 ml-2">
                            {column.render 
                              ? column.render(row[column.key], row, rowIndex)
                              : row[column.key]
                            }
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))
        )}
      </div>
    );
  }

  // Desktop table view
  return (
    <div className={`${containerClasses} ${className}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border-primary">
          {/* Header */}
          <thead className="table-header">
            <tr>
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => column.sortable && handleSort(column.key)}
                  className={`
                    ${headerCellPadding} text-left ${headerTextSize} font-medium text-text-secondary uppercase tracking-wider
                    ${column.sortable ? 'cursor-pointer hover:bg-surface-hover transition-colors duration-200' : ''}
                    ${column.width ? column.width : ''}
                    ${isMobile ? 'first:rounded-tl-xl last:rounded-tr-xl' : ''}
                  `}
                >
                  <div className="flex items-center space-x-1">
                    <span className={isMobile ? 'truncate' : ''}>{column.label}</span>
                    {column.sortable && (
                      <div className="flex flex-col">
                        <svg 
                          className={`w-3 h-3 ${
                            sortBy === column.key && sortOrder === 'asc' 
                              ? 'text-text-primary' 
                              : 'text-text-tertiary'
                          }`}
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                        </svg>
                        <svg 
                          className={`w-3 h-3 -mt-1 ${
                            sortBy === column.key && sortOrder === 'desc' 
                              ? 'text-text-primary' 
                              : 'text-text-tertiary'
                          }`}
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody className="bg-surface-elevated divide-y divide-border-primary">
            {data.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className={`${cellPadding} py-12 text-center`}>
                  <div className="flex flex-col items-center space-y-3">
                    <svg className={`${emptyIconSize} text-text-quaternary`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-text-tertiary text-sm">{emptyMessage}</p>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr 
                  key={row.id || rowIndex}
                  className="table-row"
                >
                  {visibleColumns.map((column) => (
                    <td 
                      key={column.key}
                      className={`
                        ${cellPadding} ${textSize}
                        ${isMobile ? 'whitespace-normal break-words' : 'whitespace-nowrap'}
                        ${column.mobileHidden && isMobile ? 'hidden' : ''}
                      `}
                    >
                      <div className={column.truncate && isMobile ? 'truncate' : ''}>
                        {column.render 
                          ? column.render(row[column.key], row, rowIndex)
                          : row[column.key]
                        }
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile scroll indicator */}
      {!isMobile && isTablet && (
        <div className="flex justify-center pt-2">
          <div className="text-xs text-text-tertiary">
            Scroll horizontally to see more columns
          </div>
        </div>
      )}
    </div>
  );
};

export default Table;